/**
 * API Client with Centralized Error Handling and TypeScript Support
 * Replaces scattered fetch calls with consistent, type-safe API operations
 */

import { getApi } from '../api';

import {
  APIError,
  createAPIError,
  createNetworkError,
  createAuthError,
  handleError,
} from './errorHandling';
import logger from './logger';

// Request Configuration
export interface RequestConfig extends RequestInit {
  timeout?: number;
  retries?: number;
  requiresAuth?: boolean;
}

// API Client Class
class APIClient {
  private static instance: APIClient;
  private baseURL: string;
  private defaultTimeout: number = 10000; // 10 seconds
  private defaultRetries: number = 3;

  private constructor() {
    this.baseURL = getApi();
  }

  static getInstance(): APIClient {
    if (!APIClient.instance) {
      APIClient.instance = new APIClient();
    }
    return APIClient.instance;
  }

  /**
   * Make HTTP request with automatic error handling and retries
   */
  async request<T = any>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    const {
      timeout = this.defaultTimeout,
      retries = this.defaultRetries,
      requiresAuth = true,
      ...fetchConfig
    } = config;

    const url = `${this.baseURL}${endpoint}`;

    // Add authentication token if required
    if (requiresAuth) {
      const token = this.getAuthToken();
      if (!token) {
        throw createAuthError('Authentication token not found');
      }

      // Send token as Authorization header (preferred — keeps token out of server logs and browser history)
      const headers = {
        ...(fetchConfig.headers as Record<string, string>),
        Authorization: `Bearer ${token}`,
      };

      return this.makeRequest<T>(url, { ...fetchConfig, headers }, timeout, retries);
    }

    return this.makeRequest<T>(url, fetchConfig, timeout, retries);
  }

  /**
   * GET request
   */
  async get<T = any>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  /**
   * POST request
   */
  async post<T = any>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    const body = data ? JSON.stringify(data) : undefined;
    const headers = {
      'Content-Type': 'application/json',
      ...config?.headers,
    };

    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      headers,
      body,
    });
  }

  /**
   * PUT request
   */
  async put<T = any>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    const body = data ? JSON.stringify(data) : undefined;
    const headers = {
      'Content-Type': 'application/json',
      ...config?.headers,
    };

    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      headers,
      body,
    });
  }

  /**
   * PATCH request
   */
  async patch<T = any>(endpoint: string, data?: any, config?: RequestConfig): Promise<T> {
    const body = data ? JSON.stringify(data) : undefined;
    const headers = {
      'Content-Type': 'application/json',
      ...config?.headers,
    };
    return this.request<T>(endpoint, { ...config, method: 'PATCH', headers, body });
  }

  /**
   * DELETE request
   */
  async delete<T = any>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }

  /**
   * Internal method to make the actual request with error handling
   */
  private async makeRequest<T>(
    url: string,
    config: RequestInit,
    timeout: number,
    retries: number
  ): Promise<T> {
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        const response = await fetch(url, {
          ...config,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          const errorBody = await response.text().catch(() => '');
          throw this.createHTTPError(response, url, errorBody);
        }

        // const contentType = response.headers.get('content-type');
        const text = await response.text();

        // Try to parse as JSON first, regardless of content-type
        // (Backend sometimes returns wrong content-type)
        try {
          // Check if it's base64 encoded JSON first
          if (text.startsWith('data:application/octet-stream;base64,')) {
            const base64Data = text.replace('data:application/octet-stream;base64,', '');
            const decodedData = atob(base64Data);
            const data = JSON.parse(decodedData);

            // Handle API-level errors
            if (data.error) {
              throw createAPIError(data.error, response.status, url, data);
            }

            return data;
          }

          // Try to parse as regular JSON
          const data = JSON.parse(text);

          // Handle API-level errors (success: false)
          if (data.error) {
            throw createAPIError(data.error, response.status, url, data);
          }

          return data;
        } catch {
          // If JSON parsing fails, return text as-is.
          // Callers expecting JSON should validate; some endpoints return plain text.
          return text as unknown as T;
        }
      } catch (error) {
        lastError = error as Error;

        // Don't retry on authentication errors
        if (error instanceof APIError && error.statusCode === 401) {
          throw error;
        }

        // Don't retry on client errors (4xx except 401)
        if (error instanceof APIError && error.statusCode >= 400 && error.statusCode < 500) {
          throw error;
        }

        // Wait before retry (exponential backoff)
        if (attempt < retries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 10000);
          await this.delay(delay);
          logger.debug(`Retrying request (${attempt + 1}/${retries}) after ${delay}ms`);
        }
      }
    }

    // All retries failed
    if (lastError) {
      throw this.processError(lastError, url);
    }

    throw createNetworkError('Request failed after all retries', { url });
  }

  /**
   * Create appropriate error based on HTTP response
   */
  private createHTTPError(response: Response, url: string, body: string = ''): APIError {
    let serverMessage: string | undefined;
    if (body) {
      try {
        const parsed = JSON.parse(body);
        serverMessage = parsed.message ?? parsed.error;
      } catch {
        serverMessage = body;
      }
    }
    const message = serverMessage || `HTTP ${response.status}: ${response.statusText}`;

    return createAPIError(message, response.status, url, {
      status: response.status,
      statusText: response.statusText,
      url,
    });
  }

  /**
   * Process and convert errors to appropriate types
   */
  private processError(error: Error, url: string): APIError | Error {
    if (error instanceof APIError) {
      return error;
    }

    if (error.name === 'AbortError') {
      return createNetworkError('Request timeout', { url, timeout: true });
    }

    if (error.message.includes('fetch') || error.message.includes('network')) {
      return createNetworkError(error.message, { url, originalError: error });
    }

    // Return processed error through error manager
    return handleError(error);
  }

  /**
   * Get authentication token from localStorage
   */
  private getAuthToken(): string | null {
    return window.localStorage.getItem('token');
  }

  /**
   * Utility delay function for retries
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Update base URL (useful for environment changes)
   */
  updateBaseURL(newBaseURL: string): void {
    this.baseURL = newBaseURL;
  }
}

// Singleton instance
export const apiClient = APIClient.getInstance();

/**
 * Convenience functions for common API operations
 */

// Device API
export const deviceAPI = {
  getDevices: () => apiClient.get<any[]>('/devices'),

  getTrace: (deviceId: string, start: number = 1, length: number = 0xfffffff0) =>
    apiClient.get<any[]>(
      `/trace?device=${encodeURIComponent(deviceId)}&start=${start}&length=${length}`
    ),

  addDevice: (device: any) => apiClient.post('/devices', device),

  manualAddDevice: (device: {
    title: string;
    name?: string;
    deveui: string;
    nwk_key?: string;
    device_status?: 'active' | 'reserve';
  }) => apiClient.post('/devices/manual', device),

  updateDevice: (deviceId: string, updates: any) =>
    apiClient.patch(`/devices/${encodeURIComponent(deviceId)}`, updates),

  deleteDevice: (deviceId: string) => apiClient.delete(`/devices/${encodeURIComponent(deviceId)}`),

  checkin: (deviceId: string, checkpoint: string) =>
    apiClient.post('/checkin', { device: deviceId, checkpoint }),

  uncheck: (deviceId: string) => apiClient.post('/uncheck', { device: deviceId }),

  downlink: (deviceId: string, port: number, data: string, vsm_config?: Record<string, number>) =>
    apiClient.post('/downlink', {
      device: deviceId,
      port,
      data,
      ...(vsm_config ? { vsm_config } : {}),
    }),

  clearCheckins: (checkpointId: string) =>
    apiClient.post('/clearcheckins', { checkpoint: checkpointId }),
};

// Auth API
export const authAPI = {
  // POST keeps credentials out of the URL/query string (and out of server access logs).
  login: (username: string, password: string) =>
    apiClient.post('/login', { username, password }, { requiresAuth: false }),

  // Token validation uses /ping (the /init route never existed — dead method removed, L-11).
  ping: () => apiClient.get('/ping'),
};

// User API
export const userAPI = {
  getUsers: () => apiClient.get<any[]>('/users'),

  createUser: (userData: { username: string; password?: string; rights?: string }) =>
    apiClient.post('/users', userData),

  updateUser: (
    userId: string,
    updates: { username?: string; password?: string; rights?: string }
  ) => {
    // userId is in format "user-{id}" — extract numeric ID for REST URL
    const numericId = userId.startsWith('user-') ? userId.slice(5) : userId;
    return apiClient.patch(`/users/${encodeURIComponent(numericId)}`, updates);
  },

  deleteUser: (userId: string) => {
    const numericId = userId.startsWith('user-') ? userId.slice(5) : userId;
    return apiClient.delete(`/users/${encodeURIComponent(numericId)}`);
  },
};

// Log API
export const logAPI = {
  getLogs: (limit?: number, offset?: number) => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());

    const query = params.toString();
    return apiClient.get<any[]>(`/log${query ? `?${query}` : ''}`);
  },

  getLog: () => apiClient.get<any>('/getLog'),
};

// Warning API
export const warningAPI = {
  getWarnings: (params?: { status?: string; category?: string; device_status?: string }) => {
    const query = new URLSearchParams();
    if (params?.status) query.append('status', params.status);
    if (params?.category) query.append('category', params.category);
    if (params?.device_status) query.append('device_status', params.device_status);
    const qs = query.toString();
    return apiClient.get<any>(`/maintenance/warnings${qs ? `?${qs}` : ''}`);
  },

  acknowledge: (warningId: string, note?: string | null, actionTaken: string = 'acknowledged') =>
    apiClient.post<any>('/maintenance/warnings/acknowledge', {
      warning_id: warningId,
      note: note || null,
      action_taken: actionTaken,
    }),

  acknowledgeDevice: (deviceId: string, note?: string | null) =>
    apiClient.post<any>('/maintenance/warnings/acknowledge-device', {
      device_id: deviceId,
      note: note || null,
    }),
};

// Beacon API
export const beaconAPI = {
  getBeacons: () => apiClient.get<any>('/beacons'),

  update: (beaconId: string, updates: any) =>
    apiClient.post<any>('/beacons/update', { beacon_id: beaconId, ...updates }),

  toggle: (beaconId: string, state: string) =>
    apiClient.post<any>('/beacons/toggle', { beacon_id: beaconId, state }),

  toggleDeck: (deck: number | string, state: string) =>
    apiClient.post<any>('/beacons/toggle-deck', { deck, state }),

  /** Fetch all beacon and check-in station area shapes for map overlay rendering */
  getAreas: () =>
    apiClient.get<{ success: boolean; areas: import('../types').BeaconArea[] }>('/beacons/areas'),
};

// Checkin API
const buildHistoryQuery = (filters: {
  from: string;
  to: string;
  deveui?: string;
  checkpoint?: string;
}) => {
  const q = new URLSearchParams();
  q.append('from', filters.from);
  q.append('to', filters.to);
  if (filters.deveui && filters.deveui !== 'all') q.append('deveui', filters.deveui);
  if (filters.checkpoint && filters.checkpoint !== 'all')
    q.append('checkpoint', filters.checkpoint);
  return q.toString();
};

export const checkinAPI = {
  getCheckins: () => apiClient.get<any>('/checkins'),

  update: (checkinId: string | number, updates: any) =>
    apiClient.post<any>('/checkins/update', { checkin_id: checkinId, ...updates }),

  toggle: (checkinId: number, state: string) =>
    apiClient.post<any>('/checkins/toggle', { checkin_id: checkinId, state }),

  getHistory: (filters: { from: string; to: string; deveui?: string; checkpoint?: string }) =>
    apiClient.get<{ success: boolean; history: any[] }>(
      `/checkin-history?${buildHistoryQuery(filters)}`
    ),

  /**
   * Fetch CSV export as a Blob. Uses fetch directly so we preserve the raw
   * response body and the Content-Disposition filename rather than going
   * through apiClient's JSON-first parser.
   */
  getHistoryCsvBlob: async (filters: {
    from: string;
    to: string;
    deveui?: string;
    checkpoint?: string;
  }): Promise<{ blob: Blob; filename: string }> => {
    const token = window.localStorage.getItem('token');
    const url = `${getApi()}/checkin-history/export?${buildHistoryQuery(filters)}`;
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`CSV export failed: HTTP ${res.status}`);
    const blob = await res.blob();
    const cd = res.headers.get('Content-Disposition') || '';
    const match = /filename="?([^"]+)"?/i.exec(cd);
    const filename = match ? match[1] : `checkin-history-${Date.now()}.csv`;
    return { blob, filename };
  },
};

export interface SummaryCheckpoint {
  position: number;
  deveui: string;
  description: string | null;
}

// Account-level configuration API
export const accountAPI = {
  getSummaryCheckpoints: () =>
    apiClient.get<{ success: boolean; checkpoints: SummaryCheckpoint[] }>(
      '/account/summary-checkpoints'
    ),

  setSummaryCheckpoints: (deveuis: string[]) =>
    apiClient.put<{ success: boolean; error?: string }>('/account/summary-checkpoints', {
      checkpoints: deveuis,
    }),
};

// Gateway API
export const gatewayAPI = {
  getGateways: () => apiClient.get<any>('/gateways'),

  update: (gatewayId: number, updates: any) =>
    apiClient.post<any>('/gateways/update', { gateway_id: gatewayId, ...updates }),
};

// LF Device API (infrastructure devices: beacons, checkins, gateways and LifeFinder devices)
export const lfDeviceAPI = {
  getLfDevices: (includeInactive: boolean = false) =>
    apiClient.get<any>(`/lf-devices${includeInactive ? '?include_inactive=true' : ''}`),

  updateStatus: (deviceId: string, status: string, description?: string) =>
    apiClient.post<any>('/lf-devices/update-status', { device_id: deviceId, status, description }),

  updateStatusLegacy: (deviceId: string, status: string, description?: string) =>
    apiClient.get<any>(
      `/lf-devices/update-status?device_id=${encodeURIComponent(deviceId)}&status=${encodeURIComponent(status)}${description ? `&description=${encodeURIComponent(description)}` : ''}`
    ),
};

// Settings API (maintenance thresholds)
export const settingsAPI = {
  getThresholds: () => apiClient.get<any>('/maintenance/thresholds'),

  updateThreshold: (key: string, value: number, triggerHealthCheck: boolean = false) =>
    apiClient.post<any>('/maintenance/thresholds/update', {
      setting_key: key,
      setting_value: value,
      trigger_health_check: triggerHealthCheck,
    }),
};

// Position Filter Settings API
export interface PositionFilterSetting {
  setting_key: string;
  setting_value: string | number | boolean;
  description: string;
  unit: string;
}

export const positionFilterAPI = {
  getSettings: () =>
    apiClient.get<{
      success: boolean;
      settings: PositionFilterSetting[];
      availableFilters: string[];
    }>('/position-filter/settings'),

  updateSetting: (key: string, value: string | number | boolean) =>
    apiClient.post<{ success: boolean; settings: PositionFilterSetting[] }>(
      '/position-filter/settings/update',
      { setting_key: key, setting_value: value }
    ),
};

// VSM Schema API
export interface VsmSchemaEntry {
  appName: string | null;
  schema: string | null;
  devices: string[];
}

export const vsmAPI = {
  getSchemas: () =>
    apiClient.get<{ success: boolean; schemas: Record<string, VsmSchemaEntry> }>('/vsm/schemas'),
};

// Geofence Area Config API
export const geofenceAreaConfigAPI = {
  list: () =>
    apiClient.get<{
      success: boolean;
      configs: import('../types').GeofenceAreaConfig[];
    }>('/geofence-area-configs'),

  listAreaNames: () =>
    apiClient.get<{ success: boolean; areas: string[] }>('/geofence-area-configs/areas'),

  /** @deprecated Use listAreaNames */
  listAreas: () =>
    apiClient.get<{ success: boolean; areas: string[] }>('/geofence-area-configs/areas'),

  /** Update or set the symbol for a named area (used by GeofenceSymbolsModal). */
  upsert: (area_name: string, symbol_key: string) =>
    apiClient.post<{ success: boolean; id: number; area_name: string; symbol_key: string }>(
      '/geofence-area-configs/symbol',
      { area_name, symbol_key }
    ),

  create: (data: {
    area_name: string;
    area_shape: import('../types').AreaShape;
    position_mode: 'centroid' | 'custom';
    assignments: import('../types').AreaAssignment[];
  }) =>
    apiClient.post<{ success: boolean; id: number; area_name: string }>(
      '/geofence-area-configs',
      data
    ),

  update: (
    id: number,
    data: {
      area_name: string;
      area_shape: import('../types').AreaShape;
      position_mode: 'centroid' | 'custom';
      assignments: import('../types').AreaAssignment[];
    }
  ) => apiClient.put<{ success: boolean; id: number }>(`/geofence-area-configs/${id}`, data),

  delete: (id: number) =>
    apiClient.delete<{ success: boolean; id: number }>(`/geofence-area-configs/${id}`),
};

// Devices API (reserve management and device replacement)
export const devicesAPI = {
  listReserves: (category: string) =>
    apiClient.get<any>(`/devices/list-reserves?category=${encodeURIComponent(category)}`),

  replace: (oldDeviceId: string, newDeviceId: string, replacementReason?: string | null) =>
    apiClient.post<any>('/devices/replace', {
      old_device_id: oldDeviceId,
      new_device_id: newDeviceId,
      replacement_reason: replacementReason || null,
    }),

  importDevices: (devices: DeviceImportRow[]) =>
    apiClient.post<{
      success: boolean;
      inserted: number;
      skipped: { deveui: string; reason: string }[];
      errors: { deveui: string; reason: string }[];
      chirpstackSynced: boolean;
      error?: string;
    }>('/devices/import', { devices }),

  importInfrastructure: (devices: InfraImportRow[]) =>
    apiClient.post<{
      success: boolean;
      inserted: number;
      skipped: { deveui: string; reason: string }[];
      errors: { deveui: string; reason: string }[];
      chirpstackSynced: boolean;
      error?: string;
    }>('/devices/import/infrastructure', { devices }),
};

export interface DeviceImportRow {
  deveui: string;
  name: string;
  description?: string;
  device_category?: string;
  device_status?: string;
  nwk_key?: string;
  checkpoint?: string | number | boolean;
  supplier?: string;
}

export interface InfraImportRow {
  deveui: string;
  name: string;
  description?: string;
  device_category: 'lf_beacon' | 'lf_checkin';
  device_status?: string;
  nwk_key?: string;
  checkpoint?: string | number | boolean;
  supplier?: string;
  deck?: string | number;
  side?: string;
  frame_number?: string | number;
  floor?: string | number;
  geofence?: string | number | boolean;
  geofence_area?: string;
  lat?: string | number;
  lng?: string | number;
  geofence_lat?: string | number;
  geofence_lng?: string | number;
  channel?: string | number;
  mac_address?: string;
  output_power?: string | number;
  datarate?: string | number;
}

// Solver API
export interface SolverOption {
  id: string;
  name: string;
}

export const solverAPI = {
  getSettings: () =>
    apiClient.get<{
      success: boolean;
      currentSolver: string;
      availableSolvers: SolverOption[];
    }>('/account/solver'),

  setSolver: (solver: string) =>
    apiClient.post<{ success: boolean; solver: string }>('/account/solver', { solver }),

  getTestSolvers: () =>
    apiClient.get<{
      success: boolean;
      test_solver_a: string | null;
      test_solver_b: string | null;
      availableSolvers: SolverOption[];
    }>('/account/test-solvers'),

  setTestSolvers: (test_solver_a: string | null, test_solver_b: string | null) =>
    apiClient.post<{
      success: boolean;
      test_solver_a: string | null;
      test_solver_b: string | null;
    }>('/account/test-solvers', { test_solver_a, test_solver_b }),

  getTestDevices: () =>
    apiClient.get<{
      success: boolean;
      devices: {
        deveui: string;
        name: string;
        description: string;
        device_category: string;
        test_mode: number | null;
      }[];
    }>('/account/test-devices'),

  setTestDevices: (deveuis: string[]) =>
    apiClient.post<{ success: boolean; count: number }>('/account/test-devices', { deveuis }),
};

// Operating guide API — account-level selection of which guide to show (SuperAdmin only).
export const operatingGuideAPI = {
  get: () =>
    apiClient.get<{ success: boolean; operatingGuide: string | null }>('/account/operating-guide'),

  set: (guide: string | null) =>
    apiClient.post<{ success: boolean; operatingGuide: string | null }>(
      '/account/operating-guide',
      {
        guide,
      }
    ),
};

export type DefaultCenter = { lat: number; lng: number; zoom: number | null } | null;

export const defaultCenterAPI = {
  get: () =>
    apiClient.get<{ success: boolean; defaultCenter: DefaultCenter }>('/account/defaultcenter'),

  set: (lat: number | null, lng: number | null, zoom: number | null) =>
    apiClient.patch<{ success: boolean; defaultCenter: DefaultCenter }>('/account/defaultcenter', {
      lat,
      lng,
      zoom,
    }),
};

/** Extracts the numeric user id from either a raw id or the "user-N" form. */
const userNumericId = (userId: string | number): string => {
  const s = String(userId);
  return s.startsWith('user-') ? s.slice(5) : s;
};

export const cameraLockAPI = {
  /** The authenticated user's own lock. */
  getMine: () =>
    apiClient.get<{ success: boolean; cameraLock: import('../types').CameraLock }>(
      '/account/cameralock'
    ),

  /** A specific user's lock (SuperAdmin only). */
  getForUser: (userId: string | number) =>
    apiClient.get<{ success: boolean; cameraLock: import('../types').CameraLock }>(
      `/users/${encodeURIComponent(userNumericId(userId))}/cameralock`
    ),

  /** Save or update a user's lock (SuperAdmin only). */
  setForUser: (
    userId: string | number,
    enabled: boolean,
    shape: import('../types').AreaShape,
    minZoom: number
  ) =>
    apiClient.patch<{ success: boolean; cameraLock: import('../types').CameraLock }>(
      `/users/${encodeURIComponent(userNumericId(userId))}/cameralock`,
      { enabled, shape, minZoom }
    ),

  /** Clear a user's lock entirely (shape: null) (SuperAdmin only). */
  clearForUser: (userId: string | number) =>
    apiClient.patch<{ success: boolean; cameraLock: import('../types').CameraLock }>(
      `/users/${encodeURIComponent(userNumericId(userId))}/cameralock`,
      { enabled: false, shape: null, minZoom: null }
    ),
};

export interface DisplaySettings {
  /** When true, devices with output.deviceActive=1 are shown as green markers on the map. */
  showActiveOnMap: boolean;
  /** When true, Track All / Deactivate All batch tracking buttons are shown on the map. */
  showBatchTrackingButtons: boolean;
  /** When true, the Show Trace button is shown in the device popup to display historical positions. */
  showTraceButton: boolean;
  /** Padding in pixels applied when fitting the map to a geofence area on device row click. */
  geofenceFitboundsPadding: number;
  /** When true, geofencing is applied during WiFi position solving. */
  geofencingEnabled: boolean;
  /** When true, the checkin station summary overlay is shown on the map. */
  showCheckinSummary: boolean;
  /** When true, the active/alarming device count boxes are shown on the timespace map. */
  showDeviceStatusBoxes: boolean;
  /** When true, account alarm state is published to the outbound Box2 MQTT path. */
  box2MqttEnabled: boolean;
  /** When true, web push notifications are sent to subscribed browsers on alarm state changes. */
  webPushEnabled: boolean;
  /** LoRa devEUI of the DNIL relay (downlink target). */
  relayDnilDeveui: string;
  /** DNIL relay channels — doubles as the enable flag: 0=off, 1=ch1, 2=ch2, 3=both. */
  relayDnilChannels: number;
  /** Silence window (ms) before the relay is flagged offline; falls back to the server default. */
  relaySilenceThresholdMs: number;
  /** When true, alarm e-mails are sent to users with notify_email=1 and a configured address. */
  emailNotificationsEnabled: boolean;
  /** Editable template for alarm e-mails. Placeholders: {{devices}}, {{action}}. */
  emailAlarmTemplate: string;
  /** Read-only — true when SMTP_HOST and SMTP_FROM env vars are set on the server. */
  smtpConfigured: boolean;
}

export const adminAPI = {
  disableAlarms: (deviceIds: string[]) => apiClient.post('/disable', { devices: deviceIds }),
  rejectAlarm: (deviceId: string) => apiClient.post('/reject-alarm', { device: deviceId }),
};

/** DNIL relay liveness, derived server-side the same way the maintenance job decides offline. */
export interface RelayStatus {
  /** Configured relay devEUI ('' when unset). */
  deveui: string;
  /** Channel mask / enable flag: 0=off, 1=ch1, 2=ch2, 3=both. */
  channels: number;
  /** True when a devEUI is set AND channels > 0. */
  enabled: boolean;
  /** True if heard from within the silence window; false if silent; null when disabled / no baseline. */
  online: boolean | null;
  /** Epoch ms of the last uplink heard, or null. */
  lastSeen: number | null;
  /** Epoch ms the relay was configured/enabled (silence baseline before first uplink), or null. */
  configuredAt: number | null;
  /** Epoch ms the relay last confirmed its commanded state, or null. */
  confirmedAt: number | null;
  /** Channel mask last commanded by the alarm job. */
  expectedMask: number;
  /** Silence window in ms after which the relay is considered offline. */
  silenceThresholdMs: number;
}

export const displaySettingsAPI = {
  get: () =>
    apiClient.get<{ success: boolean; settings: DisplaySettings }>('/account/display-settings'),

  update: (settings: Partial<DisplaySettings>) =>
    apiClient.patch<{ success: boolean; settings: DisplaySettings }>(
      '/account/display-settings',
      settings
    ),

  relayStatus: () =>
    apiClient.get<{ success: boolean; status: RelayStatus }>('/account/relay-status'),
};

export interface NotificationPreferences {
  email: string | null;
  notifyPush: boolean;
  notifyEmail: boolean;
}

export const notificationPreferencesAPI = {
  get: () =>
    apiClient.get<{ success: boolean; preferences: NotificationPreferences }>(
      '/account/notification-preferences'
    ),
  update: (changes: Partial<{ email: string | null; notifyPush: boolean; notifyEmail: boolean }>) =>
    apiClient.patch<{ success: boolean; preferences: NotificationPreferences }>(
      '/account/notification-preferences',
      changes
    ),
};

export const accountPasswordAPI = {
  change: (currentPassword: string, newPassword: string) =>
    apiClient.patch<{ success: boolean }>('/account/password', { currentPassword, newPassword }),
};

export const adminNotificationPreferencesAPI = {
  get: (userId: string) =>
    apiClient.get<{ success: boolean; preferences: NotificationPreferences }>(
      `/admin/users/${userNumericId(userId)}/notification-preferences`
    ),
  update: (userId: string, changes: Partial<NotificationPreferences>) =>
    apiClient.patch<{ success: boolean; preferences: NotificationPreferences }>(
      `/admin/users/${userNumericId(userId)}/notification-preferences`,
      changes
    ),
};

export const notificationsAPI = {
  getVapidPublicKey: () =>
    apiClient.get<{ success: boolean; key: string }>('/notifications/vapid-public-key'),

  getStatus: () =>
    apiClient.get<{ success: boolean; subscribed: boolean }>('/notifications/subscribe'),

  subscribe: (subscription: PushSubscription) => {
    const json = subscription.toJSON();
    return apiClient.post<{ success: boolean }>('/notifications/subscribe', {
      endpoint: json.endpoint,
      keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
    });
  },

  unsubscribe: (endpoint: string) =>
    apiClient.post<{ success: boolean }>('/notifications/unsubscribe', { endpoint }),
};

export default apiClient;
